import { PARAM_SEPERATOR } from '@/advancedStylePanel';
import { getCompRole } from './widgetDesignHelpers';
import type { EditorAPI } from '#packages/editorAPI';
import type { WidgetDesignTab } from '@wix/platform-editor-sdk';
import type { CompRef } from 'types/documentServices';
import _ from 'lodash';

interface StyleParam {
  state: string;
  type: string;
  section: number;
}

type CustomParams = Record<
  string,
  {
    mapping?: Record<string, string>;
    params?: Record<string, StyleParam>;
  }
>;

type WidgetDesignTabWithCustomParams = WidgetDesignTab & {
  customParams: CustomParams;
};

const addRichTextParamsToMapping = (
  mapping: Record<string, string>,
  customParams: CustomParams,
  compRole: string,
) => {
  Object.keys(mapping).forEach((param) => {
    if (customParams[compRole]?.params?.[param]?.type === 'RICH_TEXT') {
      mapping[`fontFamily:${param}`] = 'fontFamily';
      mapping[`fontSize:${param}`] = 'fontSize';
      mapping[`fontWeight:${param}`] = 'fontWeight';
      mapping[`fontStyle:${param}`] = 'fontStyle';
    }
  });
};

const getComponentMapping = (
  tab: WidgetDesignTabWithCustomParams,
  compRole: string,
): Record<string, string> | undefined => {
  const mapping = _.get(tab, ['customParams', compRole, 'mapping'], undefined);
  if (mapping) {
    addRichTextParamsToMapping(mapping, tab.customParams, compRole);
  }
  return mapping;
};

const getCustomParamMapping = (
  tab: WidgetDesignTabWithCustomParams,
  compRole: string,
  param: string,
): string | undefined => getComponentMapping(tab, compRole)?.[param];

const getRoleParams = (
  customParams: CustomParams,
  compRole: string,
): Record<string, StyleParam> | undefined =>
  _.get(customParams, [compRole, 'params'], undefined);

const createParamKey = (param: string, role: string): string =>
  `${param}${PARAM_SEPERATOR}${role}`;

const findComponentById = (
  components: CompRef[],
  compId: string,
): CompRef | undefined => components.find((c) => c.id === compId);

const convertMappingToOverrides = (
  mapping: Record<string, string>,
): Record<string, string[]> => _.mapValues(mapping, (value) => [value]);

export const getCustomStyleParamOverrides = (
  editorAPI: EditorAPI,
  tab: WidgetDesignTabWithCustomParams,
  comp: CompRef,
  paramKey: string,
): string[] | undefined => {
  const compRole = getCompRole(editorAPI, comp);
  const mappedParam = getCustomParamMapping(tab, compRole, paramKey);
  return mappedParam ? [mappedParam] : undefined;
};

const addRichTextParamsToSkinDef = (
  skinDef: Record<string, StyleParam>,
  roleParams: Record<string, StyleParam> | undefined,
  compRole: string,
) => {
  if (!roleParams) {
    return;
  }
  const aStyleParam = (state: string, type: string) => ({
    state,
    type,
    section: 0,
  });
  Object.entries(roleParams).forEach(([param, value]) => {
    if (value.type === 'RICH_TEXT') {
      skinDef[createParamKey(`fontFamily:${param}`, compRole)] = aStyleParam(
        value.state,
        'fontFamily',
      );
      skinDef[createParamKey(`fontSize:${param}`, compRole)] = aStyleParam(
        value.state,
        'fontSize',
      );
      skinDef[createParamKey(`fontWeight:${param}`, compRole)] = aStyleParam(
        value.state,
        'fontWeight',
      );
      skinDef[createParamKey(`fontStyle:${param}`, compRole)] = aStyleParam(
        value.state,
        'fontStyle',
      );
    }
  });
};

export const addCustomParamsToSkinDef = (
  skinDef: Record<string, StyleParam>,
  customParams: CustomParams,
  compRole: string,
) => {
  const roleParams = getRoleParams(customParams, compRole);

  _.forOwn(roleParams, (value, param) => {
    skinDef[createParamKey(param, compRole)] = value;
  });

  addRichTextParamsToSkinDef(skinDef, roleParams, compRole);
  return skinDef;
};

const getComponentOverrides = (
  editorAPI: EditorAPI,
  tab: WidgetDesignTabWithCustomParams,
  components: CompRef[],
  compId: string,
  currentOverrides: Record<string, string[]>,
): Record<string, string[]> => {
  const component = findComponentById(components, compId);
  const compRole = getCompRole(editorAPI, component);
  const customMapping = getComponentMapping(tab, compRole);

  if (customMapping) {
    const customOverrides = convertMappingToOverrides(customMapping);
    return { ...currentOverrides, ...customOverrides };
  }

  return currentOverrides;
};

export const addRichTextParamsToEditableParams = (
  editableParams: string[],
  customParams: CustomParams,
) => {
  editableParams.forEach((param) => {
    const [paramKey, role] = param.split(PARAM_SEPERATOR);
    if (customParams[role]?.params?.[paramKey]?.type === 'RICH_TEXT') {
      editableParams.push(`fontSize:${param}`);
      editableParams.push(`fontWeight:${param}`);
      editableParams.push(`fontStyle:${param}`);
      editableParams.push(`fontFamily:${param}`);
    }
  });
  return editableParams;
};

export const addCustomParamsToSkinStyleParamOverrides = (
  editorAPI: EditorAPI,
  tab: WidgetDesignTabWithCustomParams,
  components: CompRef[],
  compsStylesOverrides: Record<string, Record<string, string[]>>,
): Record<string, Record<string, string[]>> => {
  const overrides = _.mapValues(compsStylesOverrides, (overrides, compId) =>
    getComponentOverrides(editorAPI, tab, components, compId, overrides || {}),
  );
  return overrides;
};
